import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Divider, message } from 'antd';
import { PhoneOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { images } from '../assets';
import Logo from '../Component/Logo';
import LanguageSwitcher from '../Component/LanguageSwitcher';
import VerificationCode from './VerificationCode';
import ResetPassword from './ResetPassword';
import useTwilioWhatsApp from '../Hooks/useTwilioWhatsApp';
import { ForgotPasswordProps } from '../types/types';

const { Title, Text } = Typography;

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBack }) => {
  const [showVerification, setShowVerification] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { sendVerificationCode, loading } = useTwilioWhatsApp();

  const onFinish = async (values: { phone_number: string }) => {
    try {
      // Add +966 prefix to the phone number
      const fullPhoneNumber = '+966' + values.phone_number;

      // Send verification code using the hook
      const verificationCode = await sendVerificationCode(fullPhoneNumber);

      if (verificationCode) {
        setPhoneNumber(fullPhoneNumber);
        setShowVerification(true);
      }
    } catch (error) {
      console.error('Error in forgot password flow:', error);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove all non-digit characters
    value = value.replace(/[^\d]/g, '');

    // Limit to 9 digits (Saudi phone number without country code)
    if (value.length > 9) {
      value = value.substring(0, 9);
    }

    // Set the value (the +966 will be added automatically in onFinish)
    form.setFieldsValue({ phone_number: value });
  };

  // Show reset password form after verification
  if (showResetPassword) {
    return (
      <ResetPassword
        phoneNumber={phoneNumber}
        role="admin"
        onBack={() => setShowResetPassword(false)}
        onSuccess={() => {
          message.success(t('auth.passwordResetSuccess'));
          onBack(); // Go back to login
        }}
      />
    );
  }

  // Show verification code form if requested
  if (showVerification) {
    return (
      <VerificationCode
        phoneNumber={phoneNumber}
        onBack={() => setShowVerification(false)}
        onSuccess={() => {
          // After successful verification, show reset password form
          setShowResetPassword(true);
        }}
      />
    );
  }

  return (
    <div className="responsive-login">
      <div
        className="login-bg-pattern"
        style={{ backgroundImage: `url(${images.bg})` }}
        aria-hidden
      />

      <div className="login-branding">
        <div className="login-branding-inner">
          <Logo size="medium" invert={true} style={{ marginBottom: '30px' }} />
          <Title level={1} className="brand-title">
            {t('branding.DES')}
          </Title>
          <Title level={3} className="brand-subtitle">
            {t('branding.retailManagement')}
          </Title>
          <Text className="brand-tagline">{t('branding.streamlineOperations')}</Text>
        </div>
      </div>

      <div className="login-form">
        <Card>
          <div className="login-form-header">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              className="login-form-back-btn"
            />
            <Title level={2}>{t('auth.forgotPasswordTitle')}</Title>
            <Text type="secondary">{t('auth.forgotPasswordDescription')}</Text>
          </div>

          <Form
            form={form}
            name="forgotPassword"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="phone_number"
              rules={[
                { required: true, message: t('validation.required') },
                {
                  pattern: /^[0-9]{9}$/,
                  message: t('validation.invalidSaudiPhone'),
                },
              ]}
            >
              <Input
                prefix={
                  <div className="login-phone-prefix">
                    <PhoneOutlined />
                    <span>+966</span>
                  </div>
                }
                placeholder="XXXXXXXXX"
                onChange={handlePhoneChange}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: '24px' }}>
              <Button type="primary" htmlType="submit" loading={loading} block>
                {loading ? t('auth.sendingReset') : t('auth.sendResetLink')}
              </Button>
            </Form.Item>

            <Divider style={{ margin: '24px 0' }}>
              <Text type="secondary">{t('auth.secureReset')}</Text>
            </Divider>

            <div className="login-secure-note">
              <Text type="secondary">📱 {t('auth.resetInstructions')}</Text>
            </div>
          </Form>
        </Card>
      </div>

      <div className="login-language-switcher">
        <LanguageSwitcher type="button" />
      </div>
    </div>
  );
};

export default ForgotPassword;
